import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Equipamento {
  id: number;
  tipo: string;
  marca: string;
  modelo: string;
  potencia?: string;
  especificacoes?: any;
  preco_unitario?: number;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProjetoEquipamento {
  id: number;
  projeto_id?: string;
  orcamento_id?: string;
  equipamento_id: number;
  quantidade: number;
  preco_unitario?: number;
  observacoes?: string;
  created_at: string;
  updated_at: string;
  equipamento?: Equipamento;
}

export function useEquipamentos(tipo?: string) {
  return useQuery({
    queryKey: ['equipamentos', tipo],
    queryFn: async () => {
      console.log('Buscando equipamentos...');
      let query = supabase
        .from('equipamentos')
        .select('*')
        .eq('ativo', true)
        .order('marca', { ascending: true });

      if (tipo) {
        query = query.eq('tipo', tipo);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Erro ao buscar equipamentos:', error);
        throw error;
      }

      console.log('Equipamentos encontrados:', data);
      return data as Equipamento[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
  });
}

export function useCriarEquipamento() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (equipamento: {
      tipo: string;
      marca: string;
      modelo: string;
      potencia?: string;
      especificacoes?: any;
      preco_unitario?: number;
    }) => {
      console.log('Criando equipamento:', equipamento);

      // Validar dados antes de enviar
      if (!equipamento.marca || !equipamento.modelo) {
        throw new Error('Marca e modelo são obrigatórios');
      }

      if (equipamento.preco_unitario && equipamento.preco_unitario < 0) {
        throw new Error('Preço deve ser maior que zero');
      }

      const { data, error } = await supabase
        .from('equipamentos')
        .insert({
          ...equipamento,
          ativo: true
        })
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar equipamento:', error);
        
        // Traduzir erros comuns
        if (error.code === '23505') {
          throw new Error('Já existe um equipamento com esta marca e modelo');
        } else if (error.code === '23514') {
          throw new Error('Dados inválidos. Verifique os campos obrigatórios');
        } else if (error.code === '42501') {
          throw new Error('Sem permissão para criar equipamentos');
        } else {
          throw new Error(`Erro ao criar equipamento: ${error.message}`);
        }
      }

      console.log('Equipamento criado com sucesso:', data);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipamentos'] });
    }
  });
}

export function useEditarEquipamento() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...equipamento }: {
      id: number;
      tipo: string;
      marca: string;
      modelo: string;
      potencia?: string;
      especificacoes?: any;
      preco_unitario?: number;
    }) => {
      console.log('Editando equipamento:', { id, ...equipamento });

      // Validar dados antes de enviar
      if (!equipamento.marca || !equipamento.modelo) {
        throw new Error('Marca e modelo são obrigatórios');
      }

      if (equipamento.preco_unitario && equipamento.preco_unitario < 0) {
        throw new Error('Preço deve ser maior que zero');
      }

      const { data, error } = await supabase
        .from('equipamentos')
        .update({
          ...equipamento,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Erro ao editar equipamento:', error);
        
        // Traduzir erros comuns
        if (error.code === '23505') {
          throw new Error('Já existe um equipamento com esta marca e modelo');
        } else if (error.code === '23514') {
          throw new Error('Dados inválidos. Verifique os campos obrigatórios');
        } else if (error.code === '42501') {
          throw new Error('Sem permissão para editar equipamentos');
        } else {
          throw new Error(`Erro ao editar equipamento: ${error.message}`);
        }
      }

      console.log('Equipamento editado com sucesso:', data);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipamentos'] });
    }
  });
}

export function useExcluirEquipamento() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      console.log('Excluindo equipamento:', id);

      const { error } = await supabase
        .from('equipamentos')
        .update({ ativo: false })
        .eq('id', id);

      if (error) {
        console.error('Erro ao excluir equipamento:', error);
        
        if (error.code === '42501') {
          throw new Error('Sem permissão para excluir equipamentos');
        } else {
          throw new Error(`Erro ao excluir equipamento: ${error.message}`);
        }
      }

      console.log('Equipamento excluído com sucesso');
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipamentos'] });
    }
  });
}

export function useProjetoEquipamentos(projetoId?: string, orcamentoId?: string) {
  return useQuery({
    queryKey: ['projeto-equipamentos', projetoId, orcamentoId],
    queryFn: async () => {
      console.log('Buscando equipamentos do projeto/orçamento...');
      
      let query = supabase
        .from('projeto_equipamentos')
        .select(`
          *,
          equipamento:equipamentos(*)
        `)
        .order('created_at', { ascending: false });

      if (projetoId) {
        query = query.eq('projeto_id', projetoId);
      } else if (orcamentoId) {
        query = query.eq('orcamento_id', orcamentoId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Erro ao buscar equipamentos do projeto:', error);
        throw error;
      }

      console.log('Equipamentos do projeto encontrados:', data);
      return data as ProjetoEquipamento[];
    },
    enabled: !!(projetoId || orcamentoId),
    staleTime: 30000, // 30 segundos
    gcTime: 5 * 60 * 1000, // 5 minutos
  });
}

export function useAdicionarEquipamentoProjeto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dados: {
      projeto_id?: string;
      orcamento_id?: string;
      equipamento_id: number;
      quantidade: number;
      preco_unitario?: number;
      observacoes?: string;
    }) => {
      console.log('Adicionando equipamento ao projeto/orçamento:', dados);

      const { data, error } = await supabase
        .from('projeto_equipamentos')
        .insert(dados)
        .select(`
          *,
          equipamento:equipamentos(*)
        `)
        .single();

      if (error) {
        console.error('Erro ao adicionar equipamento:', error);
        throw error;
      }

      console.log('Equipamento adicionado com sucesso:', data);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ 
        queryKey: ['projeto-equipamentos', data.projeto_id, data.orcamento_id] 
      });
    }
  });
}

export function useRemoverEquipamentoProjeto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (equipamentoProjetoId: number) => {
      console.log('Removendo equipamento do projeto:', equipamentoProjetoId);

      const { error } = await supabase
        .from('projeto_equipamentos')
        .delete()
        .eq('id', equipamentoProjetoId);

      if (error) {
        console.error('Erro ao remover equipamento:', error);
        throw error;
      }

      console.log('Equipamento removido com sucesso');
      return equipamentoProjetoId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projeto-equipamentos'] });
    }
  });
}